<template>
  <div class="py-3">
    <div class="mb-2.5 flex items-baseline">
      <p :class="cn('ui-text-m mr-1.5 font-normal capitalize', labelClass)">
        {{ title }}:
      </p>
    </div>
    <div>
      <UiScrollableSlider
        slides-count="auto"
        :items-list="block.materials"
        class="-mx-4 lg:hidden"
        :gap-x="16"
      >
        <template #card="{ item }">
          <button
            class="group flex w-[94px] flex-col items-center gap-2.5 break-all border pb-2.5 pt-5 text-xxs font-light uppercase disabled:border-neutral-white-200 disabled:bg-neutral-white-100 disabled:text-primary"
            :class="
              selectedItem?.id === item.id
                ? 'border-crimson font-medium'
                : 'border-neutral-black-100'
            "
            :disabled="item.disabled"
            @click="onFilterClick(block.slug, item)"
          >
            <UiLazyImg
              class="mb-2.5 aspect-1 size-15 object-contain group-disabled:opacity-50"
              :class="[
                block.slug === propertiesGroupSlugs.diamondShape
                  ? 'transition-all md:group-hover:scale-[1.1]'
                  : '',
              ]"
              :src="
                getImageByPosition(item.medias, 'list')?.file?.url ||
                '/images/no-image.webp'
              "
              :alt="
                getImageByPosition(item.medias, 'list')?.file?.alt ||
                getFilterImageAlt({ option: item, category: block })
              "
            />
            {{ item?.currentTranslation?.title }}
          </button>
        </template>
      </UiScrollableSlider>
      <div class="relative max-lg:hidden">
        <div
          class="absolute inset-y-0 left-0 z-10 flex w-[94px] items-center justify-center bg-gradient-to-l from-white/50 from-[41%] to-white px-4 has-[button.hidden]:hidden"
          :class="swiperCarousel ? 'flex' : 'hidden'"
        >
          <button
            ref="prevEl"
            :aria-label="$t('previousSlide')"
            class="inline-flex items-center justify-center border border-gray-70 bg-off-white-10 p-2.5 transition-all"
          >
            <UiIcon name="arrow_left" class="size-6 text-primary" />
          </button>
        </div>

        <Swiper
          :slides-per-view="6"
          :space-between="16"
          :modules="modules"
          round-lengths
          :navigation="navigation"
          :breakpoints="{
            [screens.lg]: {
              spaceBetween: 16,
              slidesPerView: isRegularPdp ? 4 : 6,
            },
            [screens.xl]: {
              spaceBetween: 16,
              slidesPerView: isRegularPdp ? 4 : 6,
            },
          }"
          class="w-full"
          @swiper="onSwiperInit"
        >
          <SwiperSlide v-for="item in block.materials" :key="item.id">
            <button
              class="group flex w-[94px] flex-col items-center gap-2.5 break-all border pb-2.5 pt-5 text-xxs font-light uppercase disabled:border-neutral-white-200 disabled:bg-neutral-white-100 disabled:text-primary"
              :class="
                selectedItem?.id === item.id
                  ? 'border-crimson font-medium'
                  : 'border-neutral-black-100'
              "
              :disabled="item.disabled"
              @click="onFilterClick(block.slug, item)"
            >
              <UiLazyImg
                class="mb-2.5 aspect-1 size-15 object-contain group-disabled:opacity-50"
                :class="[
                  block.slug === propertiesGroupSlugs.diamondShape
                    ? 'transition-all md:group-hover:scale-[1.1]'
                    : '',
                ]"
                :src="
                  getImageByPosition(item.medias, 'list')?.file?.url ||
                  '/images/no-image.webp'
                "
                :alt="
                  getImageByPosition(item.medias, 'list')?.file?.alt ||
                  getFilterImageAlt({ option: item, category: block })
                "
              />
              {{ item?.currentTranslation?.title }}
            </button>
          </SwiperSlide>
        </Swiper>
        <div
          class="absolute inset-y-0 right-0 z-10 flex w-[94px] items-center justify-center bg-gradient-to-r from-white/50 from-[41%] to-white px-4 has-[button.hidden]:hidden"
          :class="swiperCarousel ? 'flex' : 'hidden'"
        >
          <button
            ref="nextEl"
            :aria-label="$t('nextSlide')"
            class="inline-flex items-center justify-center border border-gray-70 bg-off-white-10 p-2.5 transition-all"
          >
            <UiIcon name="arrow_right" class="size-6 text-primary" />
          </button>
        </div>
      </div>
    </div>
    <SharedFieldHint :message="errorMessage" error />
    <slot name="additional" />
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Swiper as SwiperInstance } from "swiper";
import { NavigationOptions } from "swiper/types";
import {
  IProductMaterialFilterEvent,
  IProductMaterialFilterProps,
  MaterialData,
} from "ecom-types";
import { screens } from "@/configs/breakpointsConfig.json";
import { propertiesGroupSlugs } from "~/configs";

const props = defineProps<
  IProductMaterialFilterProps & { isRegularPdp?: boolean }
>();

const emit = defineEmits<{
  (e: "filter", value: IProductMaterialFilterEvent): void;
}>();

const { navigation, nextEl, prevEl, modules, onSwiperInit, swiperCarousel } =
  useSwiperCarousel();

const { errorMessage, setValue } = useField(props.block?.slug, undefined, {
  initialValue: props.selectedItem?.sku,
});

function onFilterClick(slug: string, item: MaterialData) {
  setValue(item.sku);
  emit("filter", { slug, item });
}

function getFilterImageAlt({
  option,
  category,
}: {
  option: MaterialData;
  category: any;
}) {
  let res = "";
  if (category?.currentTranslation?.title) {
    res = res + category.currentTranslation.title;
  }
  if (option?.currentTranslation?.title) {
    res = res + " " + option.currentTranslation.title;
  }
  return res;
}

function useSwiperCarousel() {
  const modules = [Navigation];
  const prevEl = ref<HTMLElement | null>(null);
  const nextEl = ref<HTMLElement | null>(null);

  const swiperCarousel = ref<typeof SwiperInstance>();

  const navigation = reactive({
    prevEl,
    nextEl,
    disabledClass: "hidden",
  }) as NavigationOptions;

  function onSwiperInit(swiper: typeof SwiperInstance) {
    swiperCarousel.value = swiper;
  }

  return {
    onSwiperInit,
    swiperCarousel,
    navigation,
    prevEl,
    nextEl,
    modules,
  };
}
</script>
